import { v4 as uuidv4 } from 'uuid';

import { isDebugMode, log } from './services/logs';
import { createPlayerDom, watchContainer } from './services/horizontal-dom';
import { PixelsJS } from './types/pixelsjs';
import { getPlaylist } from './services/requests/playlist';
import { getPageUrl } from './services/common';


// our wrapper should call this.
window._horizontalPixels = async (domId: string, params: PixelsSetupParam) => {
  const {
    persistentPlayerPosition,
    adTagUrl: adTagUrlViaConfig,
    organisation,
    playerId,
    designVariant,
    integrationType
  } = __CONFIG__;

  const {
    playlist, pageId, adTagUrl: adTagUrlOverride, playlistId
  } = params;

  let playlistItems = playlist;
  if (!playlistItems) {
    playlistItems = await getPlaylist(pageId, playlistId);
  }

  const pixelsJsId = createPlayerDom(domId, playerId !== 'dual-delfi');
  const [{
    media_id: mediaId,
    prefer_captions: firstCC,
    contextual,
    headline: firstVideoHeadline,
    thumbnail: firstVideoThumbnail,
  }] = playlistItems;

  const adTagUrl = adTagUrlOverride || adTagUrlViaConfig;
  const componentSessionId = uuidv4();
  let videoIndex = 0;

  const initPlayer = () => {
    const plugins: Record<string, any> = {
      mux: {
        debug: isDebugMode(),
        data: {
          env_key: `${__MUX_ENV_KEY__}`,
          video_id: mediaId,
          custom_1: organisation,
          custom_2: params.pageId,
          custom_3: null,
          custom_4: videoIndex,
          custom_5: contextual,
          custom_7: componentSessionId,
          custom_8: null,
          custom_9: null,
          custom_10: JSON.stringify({
            player_id: playerId,
            playlist_id: playlistId
          }),
          page_url: getPageUrl(),
          experiment_name: designVariant
        }
      },
      pixelsUI: {},
      pixelsMetrics: {
        pageId,
        pageUrl: getPageUrl(),
        componentSessionId,
        designVariant,
        integrationType
      },
      preferCloseCaption: {},
      pixelsHerderBidding: {
        bidConfig: params.bidConfig,
        adTagUrl
      }
    }

    if (playerId !== 'dual-delfi') {
      plugins.persistentPlayer = {
        rootSelector: domId,
        position: persistentPlayerPosition
      }
    }

    const player = window.pixelsjs(pixelsJsId, {
      disablePictureInPicture: true,
      autoplay: true,
      controlBar: {
        pictureInPictureToggle: false,
      },
      poster: firstVideoThumbnail,
      plugins,
    });

    if (adTagUrl) {
      player.pixelsMetrics().attachPlayerEventListener();

      const options = {
        adTagUrl,
        adsManagerLoadedCallback: () => {
          player.pixelsMetrics().attachAdsEventListener();
        },
      };
      player.ima(options);

      log('pixels', 'using ad tag', adTagUrl);
    }

    if (firstCC) {
      player.preferCloseCaption().setCC(firstCC);
    }

    player.playlist(playlistItems.map(({
      media_id,
      headline,
      prefer_captions: preferCaption,
      thumbnail: poster,
      ...rest
    }) => ({
      sources: [{
        src: media_id,
        type: 'video/mux'
      }],
      poster,
      headline,
      preferCaption,
      ...rest
    })));

    if (firstVideoHeadline) {
      player.overlay({
        overlays: [{
          start: 0,
          end: 5,
          content: firstVideoHeadline,
        }],
      });
    }

    player.playlist.autoadvance(0);

    // request ads inside playlist
    player.on('beforeplaylistitem', async (_event: string, item: PlaylistItem) => {
      player.ima.controller.setSetting('adWillPlayMuted', player.muted());

      // only replace ad tag when header bidding is enabled
      const adTagUrl = await player.pixelsHerderBidding().getAdTagUrl();
      player.ima.setContentWithAdTag(item.sources, adTagUrl);
      player.ima.requestAds();

      // switch mux metrics
      player.mux.emit('videochange', {
        video_id: item.sources[0].src,
        custom_4: ++videoIndex,
        custom_5: item.contextual
      });

      // update title overlay
      player.overlay({
        overlays: [{
          start: 0,
          end: 5,
          content: item.headline,
        }],
      });

      if (item.preferCaption) {
        player.preferCloseCaption().setCC(item.preferCaption);
      }
    });

    log('pixels', 'inited player to play media id', mediaId);

    return player;
  };

  const getPlayer = (() => {
    let player: PixelsJS;
    return (isIntersecting: boolean) => {
      if (!player && !isIntersecting) {
        return null;
      }

      if (!player) {
        player = initPlayer();
        return player;
      }

      return player;
    };
  })();

  watchContainer(document.getElementById(domId) as Element, (isIntersecting) => {
    const player = getPlayer(isIntersecting);
    if (!player) return;

    if (isIntersecting) {
      player.play().catch((ex) => {
        console.error('pixels-js unable to play the video', ex);
      });

    } else {
      player.pause();
    }
  }, { rootMargin: '0px' });
};
